<template>
  <div class="card">
    <div
      class="card-header bg-transparent border-0 pb-0 d-flex align-items-start justify-content-between gap-3"
    >
      <div>
        <h5 class="mb-1">{{ $t("forwards.overview.deliveries.title") }}</h5>
        <p class="text-body-tertiary fs-10 mb-0">
          {{ $t("forwards.overview.deliveries.description") }}
        </p>
      </div>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary text-nowrap"
        :disabled="downloading"
        data-testid="download-deliveries"
        @click="downloadSpreadsheet"
      >
        <FontAwesomeIcon icon="fa-regular fa-download" class="me-1" />
        {{ $t("forwards.overview.deliveries.download_xlsx") }}
      </button>
    </div>
    <div class="card-body">
      <div class="row g-2 align-items-end mb-3">
        <div class="col-12 col-md-4">
          <label class="eyebrow-label eyebrow-label--sm mb-1" :for="dealNameId">
            {{ $t("forwards.overview.deliveries.filters.deal_name") }}
          </label>
          <input
            :id="dealNameId"
            v-model="dealNameInput"
            type="search"
            :maxlength="DEAL_FILTER_TEXT_MAX"
            class="form-control form-control-sm"
            :placeholder="
              $t('forwards.overview.deals.filters.name_placeholder')
            "
          />
        </div>
        <div class="col-12 col-md-4">
          <label class="eyebrow-label eyebrow-label--sm mb-1" :for="statusId">
            {{ $t("forwards.overview.deliveries.filters.status") }}
          </label>
          <MultiSelectComponent
            v-model="statuses"
            :options="statusOptions"
            :buttonId="statusId"
            :placeholder="$t('forwards.overview.deals.filters.status_all')"
            size="sm"
          />
        </div>
        <div class="col-12 col-md-4">
          <label class="eyebrow-label eyebrow-label--sm mb-1" :for="windowId">
            {{ $t("forwards.overview.deliveries.filters.window_end") }}
          </label>
          <DatePicker
            v-model="pickedWindow"
            :inputId="windowId"
            selectionMode="range"
            :manualInput="false"
            dateFormat="dd/mm/yy"
            :placeholder="
              $t('forwards.overview.deliveries.filters.window_end_any')
            "
            showButtonBar
            appendTo="body"
            class="w-100"
            @date-select="applyPickedWindow"
            @clear-click="clearWindow"
          />
        </div>
      </div>

      <LoadingIndicatorComponent
        v-if="loading"
        :message="$t('forwards.overview.deliveries.loading')"
      />

      <RetryableErrorNoteComponent
        v-else-if="errorMessage"
        :message="errorMessage"
        @retry="emit('retry')"
      />

      <p v-else-if="rows.length === 0" class="text-body-tertiary mb-0 py-3">
        {{ $t("forwards.overview.deliveries.empty") }}
      </p>

      <div v-else class="table-responsive">
        <table class="table align-middle mb-0">
          <thead>
            <tr>
              <th scope="col">
                <span class="eyebrow-label eyebrow-label--sm">
                  {{ $t("forwards.overview.deliveries.transaction") }}
                </span>
              </th>
              <th scope="col">
                <span class="eyebrow-label eyebrow-label--sm">
                  {{ $t("forwards.overview.deliveries.window") }}
                </span>
              </th>
              <th scope="col">
                <span class="eyebrow-label eyebrow-label--sm">
                  {{ $t("forwards.overview.deliveries.deal") }}
                </span>
              </th>
              <th scope="col">
                <span class="eyebrow-label eyebrow-label--sm">
                  {{ $t("forwards.overview.deliveries.escrowed_of_planned") }}
                </span>
              </th>
              <th scope="col" class="text-end">
                <span class="eyebrow-label eyebrow-label--sm">
                  {{ $t("forwards.common.actions") }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.delivery_id">
              <td>
                <RouterLink :to="transactionRoute(row)" class="text-nowrap">
                  #{{ row.sales_transaction_id }}
                </RouterLink>
                <div v-if="row.transaction_status !== 'DRAFT'" class="mt-1">
                  <span
                    class="badge rounded-pill"
                    :class="transactionStatusClass(row.transaction_status)"
                  >
                    {{ transactionStatusLabel(row.transaction_status) }}
                  </span>
                </div>
              </td>
              <td>
                <div class="text-nowrap">{{ windowLabel(row) }}</div>
                <div
                  v-if="windowNote(row) !== null"
                  class="fs-10"
                  :class="isOverdue(row) ? 'text-danger' : 'text-body-tertiary'"
                >
                  {{ windowNote(row) }}
                </div>
              </td>
              <td>
                <RouterLink :to="dealRoute(row.deal_id)">
                  {{ row.deal_name }}
                </RouterLink>
                <ForwardsStatusBadgeComponent
                  v-if="row.is_pay_as_produced"
                  status="pay_as_produced"
                  class="ms-2"
                />
                <ForwardsStatusBadgeComponent
                  v-if="row.deal_status === 'cancelled'"
                  status="cancelled"
                  class="ms-2"
                />
                <div class="text-body-tertiary fs-10">
                  {{
                    row.periods.length > 0
                      ? row.periods.join(" · ")
                      : $t("forwards.common.none")
                  }}
                </div>
              </td>
              <td class="delivery-fill">
                <EscrowFillBarComponent
                  :escrowed="row.escrowed_mwh"
                  :total="row.planned_mwh"
                  :showShortfall="!row.is_pay_as_produced"
                  :caption="fillCaption(row)"
                />
                <div
                  v-if="row.missing_by_country.length > 0"
                  class="d-flex flex-wrap align-items-center gap-1 mt-1"
                >
                  <CountryPillComponent
                    v-for="group in row.missing_by_country"
                    :key="group.countries.join('/')"
                    :country="group.countries"
                    :volumeMwh="group.mwh"
                  />
                  <span class="text-body-tertiary fs-10">
                    {{ $t("forwards.overview.deliveries.short") }}
                  </span>
                </div>
              </td>
              <td class="text-end">
                <div class="d-inline-flex gap-1">
                  <RouterLink
                    :to="transactionRoute(row)"
                    class="btn btn-sm btn-outline-secondary text-nowrap"
                    data-testid="open-transaction"
                  >
                    {{ $t("forwards.overview.deliveries.open_transaction") }}
                  </RouterLink>
                  <RouterLink
                    :to="dealRoute(row.deal_id)"
                    class="btn btn-sm btn-outline-secondary text-nowrap"
                    data-testid="open-deal"
                  >
                    {{ $t("forwards.overview.deliveries.open_deal") }}
                  </RouterLink>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import {
  formatDate,
  formatDateTimeIntoIsoDate,
  formatNumberWithSpaces,
} from "@/composables/formatUtils";
import CountryPillComponent from "@/forwards2/components/CountryPillComponent.vue";
import EscrowFillBarComponent from "@/forwards2/components/EscrowFillBarComponent.vue";
import ForwardsStatusBadgeComponent from "@/forwards2/components/ForwardsStatusBadgeComponent.vue";
import RetryableErrorNoteComponent from "@/forwards2/components/RetryableErrorNoteComponent.vue";
import { dealRoute } from "@/forwards2/deals/dealRoute";
import { forwardsApi, forwardsErrorMessage } from "@/forwards2/forwardsApi";
import {
  DEAL_FILTER_TEXT_MAX,
  TRANSACTION_STATUSES,
  type TransactionStatus,
  type UpcomingDeliveriesQuery,
  type UpcomingDeliveryRow,
} from "@/forwards2/forwardsTypes";
import {
  downloadFromBlob,
  extractFilenameFromContentDisposition,
} from "@/utils/downloadUtils";
import { refDebounced } from "@vueuse/core";
import { parseISO } from "date-fns";
import DatePicker from "primevue/datepicker";
import { computed, inject, ref, useId, watch } from "vue";
import { RouterLink } from "vue-router";
import { useI18n } from "vue-i18n";

/**
 * The confirmed and completed deals' deliveries, nearest window end first, narrowed by the filters
 * above the table. The endpoint leaves draft deals out, not a filter. It leaves cancelled deals out
 * too, except the deliveries they shipped before the cancellation: those were invoiced, so they stay
 * listed with a cancelled badge.
 *
 * The download button ignores the filters: the spreadsheet is every delivery the card can list, for
 * accounting to reconcile the balance against.
 *
 * Filters are owned by the caller through `v-model:filters` so they can live in the URL. The status
 * filter is the transaction's, the same status the badge on each row shows. The deal name reaches
 * the model debounced, and the window only once both of its ends are picked — each
 * change is a request and a history entry.
 *
 * The missing volume is what the delivery's escrow still lacks against its planned volume, grouped
 * by the device countries of the pools that committed it: one pill per group, and a group that
 * spans countries stays one pill ("EE/LV 640") because a pool-level shortfall can be filled from
 * any of that pool's countries and splitting it would invent a precision the number does not have.
 *
 * A pay-as-produced month is measured against its nominated target, which is monitoring only: it
 * owes no fixed volume, so its bar carries no hatched shortfall and its row no country pills.
 */
const props = withDefaults(
  defineProps<{
    rows: UpcomingDeliveryRow[];
    loading?: boolean;
    error?: unknown;
  }>(),
  { loading: false }
);

const emit = defineEmits<{
  retry: [];
}>();

const filters = defineModel<UpcomingDeliveriesQuery>("filters", {
  required: true,
});

const { t } = useI18n();

const globalErrorHandler = inject("globalErrorHandler") as (
  error: unknown
) => void;

const FILTER_DEBOUNCE_MS = 350;

// Bootstrap subtle pairs, mirroring how the admin transactions table colours the same statuses.
const TRANSACTION_STATUS_CLASSES: Record<TransactionStatus, string> = {
  DRAFT: "bg-secondary-subtle text-secondary-emphasis",
  PENDING_BUYER_DETAILS: "bg-info-subtle text-info-emphasis",
  PENDING_CONTRACTS: "bg-warning-subtle text-warning-emphasis",
  READY_TO_TRANSFER: "bg-primary-subtle text-primary-emphasis",
  DELIVERED: "bg-info-subtle text-info-emphasis",
  COMPLETED: "bg-success-subtle text-success-emphasis",
};

const statusId = useId();
const windowId = useId();
const dealNameId = useId();

const downloading = ref(false);

const dealNameInput = ref(filters.value.dealName);
const debouncedDealName = refDebounced(dealNameInput, FILTER_DEBOUNCE_MS);

/** The picker's own selection: it holds a half-picked range until the second click. */
const pickedWindow = ref<(Date | null)[] | null>(windowAsDates(filters.value));

const statuses = computed<TransactionStatus[]>({
  get: () => filters.value.transactionStatus,
  set: (transactionStatus) => {
    filters.value = { ...filters.value, transactionStatus };
  },
});

const statusOptions = computed(() =>
  TRANSACTION_STATUSES.map((value) => ({
    value,
    label: transactionStatusLabel(value),
  }))
);

const errorMessage = computed(() => {
  if (props.error === null || props.error === undefined) return null;
  return (
    forwardsErrorMessage(props.error) ??
    t("forwards.overview.deliveries.load_failed")
  );
});

watch(debouncedDealName, (dealName) => {
  if (dealName === filters.value.dealName) return;
  filters.value = { ...filters.value, dealName };
});

// The URL is the filters' home, so the back button and a pasted link both move them under us.
watch(filters, (next) => {
  if (next.dealName !== dealNameInput.value)
    dealNameInput.value = next.dealName;
  pickedWindow.value = windowAsDates(next);
});

const applyPickedWindow = () => {
  const [from, to] = pickedWindow.value ?? [];
  if (!from || !to) return;
  filters.value = {
    ...filters.value,
    windowEndFrom: formatDateTimeIntoIsoDate(from),
    windowEndTo: formatDateTimeIntoIsoDate(to),
  };
};

const clearWindow = () => {
  filters.value = { ...filters.value, windowEndFrom: null, windowEndTo: null };
};

const downloadSpreadsheet = async () => {
  downloading.value = true;
  try {
    const response = await forwardsApi.downloadUpcomingDeliveries();
    const disposition: unknown = response.headers["content-disposition"];
    const filename =
      extractFilenameFromContentDisposition(
        typeof disposition === "string" ? disposition : undefined
      ) ?? `forwards-deliveries-${formatDateTimeIntoIsoDate(new Date())}.xlsx`;
    downloadFromBlob(response.data, filename);
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    downloading.value = false;
  }
};

const transactionRoute = (row: UpcomingDeliveryRow): string =>
  `/admin/transactions/${row.sales_transaction_id}/edit`;

const windowLabel = (row: UpcomingDeliveryRow): string =>
  row.window_start === null
    ? t("forwards.overview.deliveries.on_or_before", {
        date: formatDate(row.window_end),
      })
    : `${formatDate(row.window_start)} → ${formatDate(row.window_end)}`;

/** Null keeps the fill bar's own "x of y MWh" caption. */
const fillCaption = (row: UpcomingDeliveryRow): string | null =>
  row.is_pay_as_produced
    ? t("forwards.overview.deliveries.escrowed_of_target", {
        escrowed: formatNumberWithSpaces(row.escrowed_mwh),
        target: formatNumberWithSpaces(row.planned_mwh),
      })
    : null;

// Once a transaction is DELIVERED the certificates have left, so the window countdown no longer
// applies: a DELIVERED row waits on the buyer's payment (D-08-09), a COMPLETED one on nothing.
const isShipped = (row: UpcomingDeliveryRow): boolean =>
  row.transaction_status === "DELIVERED" ||
  row.transaction_status === "COMPLETED";

const isOverdue = (row: UpcomingDeliveryRow): boolean =>
  row.days_left < 0 && !isShipped(row);

/** Null for a completed row: its status pill already says all there is. */
const windowNote = (row: UpcomingDeliveryRow): string | null => {
  if (row.transaction_status === "DELIVERED")
    return t("forwards.overview.deliveries.delivered_awaiting_completion");
  if (row.transaction_status === "COMPLETED") return null;
  return dueLabel(row.days_left);
};

/** Plain wording per bucket rather than plural forms — "in 1 days" reads wrong in every language. */
const dueLabel = (daysLeft: number): string => {
  if (daysLeft < -1)
    return t("forwards.overview.deliveries.overdue_days", {
      days: Math.abs(daysLeft),
    });
  if (daysLeft === -1) return t("forwards.overview.deliveries.overdue_one_day");
  if (daysLeft === 0) return t("forwards.overview.deliveries.due_today");
  if (daysLeft === 1) return t("forwards.overview.deliveries.due_tomorrow");
  return t("forwards.overview.deliveries.due_in_days", { days: daysLeft });
};

const transactionStatusLabel = (status: TransactionStatus): string => {
  const key = `forwards.overview.deliveries.transaction_status.${status.toLowerCase()}`;
  const translated = t(key);
  return translated === key ? status : translated;
};

const transactionStatusClass = (status: TransactionStatus): string =>
  TRANSACTION_STATUS_CLASSES[status] ??
  "bg-secondary-subtle text-secondary-emphasis";

/** `parseISO` reads a bare date as local midnight, the day the picker shows. */
function windowAsDates(query: UpcomingDeliveriesQuery): (Date | null)[] | null {
  if (query.windowEndFrom === null && query.windowEndTo === null) return null;
  return [
    query.windowEndFrom === null ? null : parseISO(query.windowEndFrom),
    query.windowEndTo === null ? null : parseISO(query.windowEndTo),
  ];
}
</script>

<style scoped lang="scss">
.delivery-fill {
  min-width: 14rem;
}
</style>
